import React, { useRef, useState, useEffect } from 'react';
import { useAcademyStore } from '../../store/academyStore';
import { useNavigate } from 'react-router-dom';
import { Download, ArrowLeft, Award, Shield, Code, Database, Globe, Cpu, Lock, Star, Trophy, Map } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Reuse the meta dictionary or import it ideally. Since it's in UserProfile without export, we duplicate or re-declare here cleanly.
const badgesMeta: Record<string, { title: string, desc: string, icon: any, image: string }> = {
    'badge_digital-citizen': { title: 'Digital Citizen', desc: 'Stayed safe online!', icon: <Shield />, image: 'badge_digital-citizen.png' },
    'badge_foundation': { title: 'Foundation Explorer', desc: 'Mastered the basics.', icon: <Code />, image: 'badge_foundation.png' },
    'badge_production': { title: 'City Planner', desc: 'Built scalable systems.', icon: <Database />, image: 'badge_production.png' },
    'badge_data-detective': { title: 'Data Detective', desc: 'Solved the SQL mystery.', icon: <Globe />, image: 'badge_data-detective.png' },
    'badge_ai': { title: 'AI Operator', desc: 'Tamed the robots and agents.', icon: <Cpu />, image: 'badge_ai.png' },
    'badge_cyber-security': { title: 'Digital Guardian', desc: 'Protected the network.', icon: <Lock />, image: 'badge_cyber-security.png' },
    'badge_automation': { title: 'Bot Builder', desc: 'Automated the workflow.', icon: <Cpu />, image: 'badge_automation.png' },
    'badge_ui-ux': { title: 'Design Artist', desc: 'Painted the pixels.', icon: <Star />, image: 'badge_ui-ux.png' },
    'badge_systems': { title: 'Sovereign Ruler', desc: 'Gained control of the server rack.', icon: <Shield />, image: 'badge_systems.png' },
    'badge_game-dev': { title: 'Game Maker', desc: 'Built your first game!', icon: <Trophy />, image: 'badge_game-dev.png' },
    'badge_variables': { title: 'Backpack Packer', desc: 'Packed the data.', icon: <Code />, image: 'badge_variables.png' },
    'badge_loops': { title: 'Loop Runner', desc: 'Ran the same track 100 times.', icon: <Code />, image: 'badge_loops.png' },
    'badge_architecture': { title: 'Map Reader', desc: 'Understands the client & server.', icon: <Map />, image: 'badge_architecture.png' },
    'badge_functions': { title: 'Recipe Chef', desc: 'Cooked up some logic.', icon: <Code />, image: 'badge_functions.png' },
    'badge_relational': { title: 'Architect', desc: 'Designed the blueprint.', icon: <Database />, image: 'badge_relational.png' },
    'badge_logic-architect': { title: 'Logic Architect', desc: 'Mastered React state and components.', icon: <Code />, image: 'badge_logic-architect.png' },
};

export const BadgeExportView = () => {
    const { currentUser, progress } = useAcademyStore();
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [scale, setScale] = useState(1);

    // Fit to screen visually 
    useEffect(() => {
        const handleResize = () => {
            const viewportHeight = window.innerHeight - 100;
            // PDF is 800x1100 px aspect ratio (portrait 8x11)
            const targetHeight = 1100; 
            const scale = viewportHeight / targetHeight;
            setScale(Math.min(scale, 1.0)); // don't zoom over 1x
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!currentUser) return <div className="p-10">Access Denied. Modifying the URL parameters directly?</div>;

    const userProgress = progress[currentUser.id] || { badges: [] };
    const earnedBadges = userProgress.badges || [];

    // Filter to only show badges the user actually unlocked (or show all with locked states? User said "export badge collection" which usually means what they have).
    // Let's show all earned badges in a tight grid.
    const earnedMeta = Object.keys(badgesMeta)
        .filter(key => earnedBadges.includes(key))
        .map(key => badgesMeta[key]);

    const handleDownload = async () => {
        if (!containerRef.current || !currentUser) return;
        setIsGenerating(true);
        
        const currentScale = scale;
        setScale(1);
        await new Promise(r => setTimeout(r, 150));

        try {
            // Generate canvas with 2x scale for print resolution
            const canvas = await html2canvas(containerRef.current, {
                scale: 2, 
                useCORS: true,
                backgroundColor: '#f8fafc', // slate-50
                logging: false,
                allowTaint: true
            });
            
            const imgData = canvas.toDataURL('image/jpeg', 0.95);
            
            const aspect = canvas.height / canvas.width;
            const pdfHeight = Math.max(1100, 800 * aspect); // Guarantee at least 1100px tall
            
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'px',
                format: [800, pdfHeight]
            });
            
            pdf.addImage(imgData, 'JPEG', 0, 0, 800, pdfHeight);
            pdf.save(`${currentUser.username.replace(/\s+/g, '_')}_Merit_Badges.pdf`);
        } catch (err) {
            console.error('Failed to generate PDF', err);
            alert("Sorry, an error occurred generating the document.");
        } finally {
            setScale(currentScale);
            setIsGenerating(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col items-center py-10 selection:bg-none font-sans overflow-x-hidden relative">
            {/* Top Navigation */}
            <div className="fixed top-0 left-0 right-0 p-4 flex justify-between items-center z-50 pointer-events-none">
                <button 
                    onClick={() => navigate('/profile')}
                    className="pointer-events-auto flex items-center gap-2 bg-slate-800/80 hover:bg-slate-700 backdrop-blur-md text-white px-4 py-2 rounded-full border border-slate-600 transition-colors shadow-lg"
                >
                    <ArrowLeft size={16} /> Return to Profile
                </button>

                <button 
                    onClick={handleDownload}
                    disabled={isGenerating || earnedMeta.length === 0}
                    className="pointer-events-auto flex items-center gap-2 bg-green-500 hover:bg-green-400 text-white font-bold px-6 py-3 rounded-full shadow-lg hover:shadow-green-500/50 shadow-green-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isGenerating ? 'Generating PDF...' : (
                        <>
                            <Download size={20} />
                            Download Badges PDF
                        </>
                    )}
                </button>
            </div>

            {/* Scale wrapper for responsive preview */}
            <div 
                className="transition-transform duration-300 origin-top shadow-2xl mt-12"
                style={{ transform: `scale(${scale})` }}
            >
                {/* The Actual PDF Target Container (Dynamic Height) */}
                <div 
                    ref={containerRef}
                    className="bg-slate-50 relative overflow-hidden" 
                    style={{ width: '800px', minHeight: '1100px' }}
                >
                    {/* Background Florishes */}
                    <div className="absolute top-0 right-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl -ml-40 -mb-40 pointer-events-none"></div>

                    {/* Content Boundary */}
                    <div className="relative z-10 p-12 flex flex-col items-center min-h-full">
                        
                        {/* Header Box */}
                        <div className="w-full text-center border-b-2 border-slate-200 pb-8 mb-10 pt-4">
                            <div className="flex items-center justify-center gap-4 mb-4">
                                <Award className="w-16 h-16 text-yellow-500" />
                                <h1 className="text-4xl font-black font-comic tracking-wider text-slate-800 uppercase">
                                    Official Merit Record
                                </h1>
                                <Award className="w-16 h-16 text-yellow-500" />
                            </div>
                            <h2 className="text-2xl text-slate-600 font-serif italic mb-2">
                                Presented to Scout: <strong className="text-slate-800 font-sans not-italic">{currentUser.username}</strong>
                            </h2>
                            <p className="text-sm font-mono text-slate-400 uppercase tracking-widest">
                                Grounded Growth Academy • {new Date().getFullYear()} Certification
                            </p>
                        </div>

                        {/* Badges Grid (Responsive to amount) */}
                        {earnedMeta.length === 0 ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 opacity-50">
                                <Shield size={80} className="mb-4" />
                                <p className="text-xl font-bold font-comic">No Merit Badges Earned Yet</p>
                            </div>
                        ) : (
                            <div className="w-full grid grid-cols-4 gap-x-4 gap-y-8 relative z-10 p-4">
                                {earnedMeta.map((badge, idx) => (
                                    <div key={idx} className="flex flex-col items-center text-center">
                                        <div className="w-32 h-32 rounded-full bg-white shadow-md border-4 border-white overflow-hidden mb-3 relative flex items-center justify-center">
                                            {/* Drop shadow explicitly on the image for depth */}
                                            <img 
                                                src={`/assets/badges/${badge.image}`} 
                                                alt={badge.title}
                                                className="w-full h-full object-cover transform scale-[1.15]" 
                                            />
                                        </div>
                                        <h3 className="font-bold text-slate-800 font-comic text-sm uppercase tracking-wide leading-tight">
                                            {badge.title}
                                        </h3>
                                        <p className="text-[10px] text-slate-500 mt-1 max-w-[150px]">
                                            {badge.desc}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Footer Spacer */}
                        <div className="mt-auto pt-10 w-full text-center opacity-40">
                            <p className="text-[10px] font-mono text-slate-500">Document universally verified via cryptographic integrity metrics. Official Academy Document.</p>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};
